import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Stack,
  VStack,
  Heading,
  Icon,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { completeKYC } from "../Redux/Action/kyc/completeKYCAction";
import { useDispatch, useSelector } from "react-redux";
import { SlCloudUpload } from "react-icons/sl";
import { FiX } from "react-icons/fi";
import { FaRegTrashAlt } from "react-icons/fa";
import { ImagePreview } from "../Components/custom/ImagePreview";
import { Stepper } from "../Components/custom/Stepper";
import { CommonButton } from "../Components/ui/CommonButton";
import { getDomainConfig } from "../Utils/dynamicBranding";
import { useNavigate } from "react-router-dom";

export const CompleteKYC = () => {
  useEffect(() => {
    const config = getDomainConfig();
    document.title = `Complete KYC - ${config.title}`;
  }, []);

  const [kycImages, setKycImages] = useState({
    front: null,
    back: null,
    other: null,
  });
  const [isDisabled, setIsDisabled] = useState(true);
  const [dragOver, setDragOver] = useState({});
  const [timer, setTimer] = useState(45); // 45 seconds
  const [showTimer, setShowTimer] = useState(false);
  const timerRef = useRef(null);
  const fileInputRefs = useRef({});
  const userData = useSelector((state) => state?.Login?.userData);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Responsive values
  const pagePaddingX = useBreakpointValue({ base: 4, sm: 6, md: 8 });
  const pagePaddingY = useBreakpointValue({ base: 2, sm: 4, md: 6 });
  const formPadding = useBreakpointValue({ base: 4, md: 10 });
  const buttonWidth = useBreakpointValue({ base: "100%", md: "auto" });

  // Timer effect
  useEffect(() => {
    if (showTimer && timer > 0) {
      timerRef.current = setTimeout(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (showTimer && timer === 0) {
      // Timer completed - clear local storage and redirect to login
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("userData");
      
      // Force a page refresh to ensure clean state
      window.location.href = "/login";
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [showTimer, timer]);

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Clear local storage and navigate
  const clearLocalStorageAndNavigate = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    
    // Force a page refresh to ensure clean state
    window.location.href = "/login";
  };

  useEffect(() => {
    const anyImagePresent = kycImages.front || kycImages.back || kycImages.other;
    setIsDisabled(!anyImagePresent);
  }, [kycImages]);

  const handleFileChange = (e, type) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setKycImages((prev) => ({
      ...prev,
      [type]: file,
    }));
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: false }));
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        setKycImages((prev) => ({
          ...prev,
          [type]: file,
        }));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, type) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: false }));
  };

  const triggerFileInput = (type) => {
    if (fileInputRefs.current[type]) {
      fileInputRefs.current[type].click();
    }
  };

  const handleSubmit = () => {
    if (!isDisabled) {
      const formData = new FormData();
      formData.append("front", kycImages.front);
      formData.append("back", kycImages.back || "");
      formData.append("other", kycImages.other || "");
      setIsDisabled(true);
   
      dispatch(
        completeKYC({
          payload: formData,
          callback: (data) => {
         
            if (data?.meta?.code === 200) {
           
              setShowTimer(true);
            } else {
              // Re-enable the submit button if submission failed
              setIsDisabled(false);
            }
          },
        })
      );
    }
  };

  const kycFields = [
    { type: "front", label: "Proof of Identity (Front)" },
    { type: "back", label: "Proof of Identity (Back)" },
    { type: "other", label: "Proof of Address" },
  ];

  const renderUploadFields = [];

  for (let i = 0; i < kycFields.length; i++) {
    const { type, label } = kycFields[i];
    renderUploadFields.push(
      <Box 
        key={type} 
        maxW="lg" 
        w="100%"
        transition="all 0.3s ease"
      >
        {kycImages?.[type] ? (
          <VStack
            padding="1.5rem"
            border="2px dashed #09579a"
            borderRadius="16px"
            height="100%"
            alignContent="center"
            backgroundColor="rgba(9, 87, 154, 0.05)"
            position="relative"
            _hover={{
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              transform: "translateY(-5px)",
              cursor: "pointer"
            }}
            cursor="pointer"
            onClick={() => triggerFileInput(type)}
            width="100%"
          >
            <Box width="100%" textAlign="center" mb="0.5rem">
              <Text fontWeight="600" color="#09579a" fontSize="sm">
                {label}
              </Text>
            </Box>
            
            <Box position="absolute" top="1rem" right="1rem" display="flex" gap={2}>
              <Button
                size="sm"
                variant="solid"
                colorScheme="red"
                backgroundColor="#e53e3e"
                borderRadius="full"
                minWidth="auto"
                height="48px"
                width="48px"
                padding={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 4px 8px rgba(0, 0, 0, 0.2)"
                _hover={{
                  backgroundColor: "#c53030",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(0, 0, 0, 0.25)"
                }}
                _active={{
                  backgroundColor: "#9b2c2c",
                  transform: "translateY(0)"
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setKycImages(prev => ({ ...prev, [type]: null }));
                }}
              >
                <Icon as={FaRegTrashAlt} boxSize={6} color="white" />
              </Button>
            </Box>
            
            <ImagePreview src={kycImages[type]} type={type} label={label} />

            <Box alignItems="center" mt="auto" textAlign="center">
              <Button 
                variant="solid"
                size="md"
                colorScheme="blue"
                borderRadius="8px"
                fontWeight="600"
                boxShadow="md"
                paddingX={6}
                paddingY={2}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg"
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  triggerFileInput(type);
                }}
              >
                Re-upload
              </Button>
            </Box>
            
            <input
              type="file"
              ref={(el) => (fileInputRefs.current[type] = el)}
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFileChange(e, type)}
            />
          </VStack>
        ) : (
          <VStack 
            spacing={4} 
            height="100%" 
            justifyContent="center"
            padding="1.5rem"
            borderRadius="16px"
            transition="all 0.3s ease"
            _hover={{
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              transform: "translateY(-5px)",
              cursor: "pointer"
            }}
            backgroundColor={dragOver[type] ? "blue.50" : "white"}
            border={`2px dashed ${dragOver[type] ? "#09579a" : "#cbd5e0"}`}
            cursor="pointer"
            onClick={() => triggerFileInput(type)}
            onDrop={(e) => handleDrop(e, type)}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, type)}
            onDragLeave={(e) => handleDragLeave(e, type)}
            width="100%"
          >
            <Box width="100%" textAlign="center" mb="0.5rem">
              <Text fontWeight="600" color="#2d3748" fontSize="sm">
                {label}
              </Text>
            </Box>
            
            <Icon 
              as={SlCloudUpload} 
              boxSize={12} 
              color="#09579a" 
              opacity={0.7}
            />
            
            <VStack spacing={1}>
              <Text fontWeight="600" color="#2d3748">
                Drag & drop files here
              </Text>
              <Text color="gray.500" fontSize="sm">
                or
              </Text>
            </VStack>
            
            <Button 
              variant="solid" 
              size="sm"
              colorScheme="blue"
              borderRadius="8px"
              fontWeight="500"
              onClick={(e) => {
                e.stopPropagation();
                triggerFileInput(type);
              }}
            >
              Browse Files
            </Button>
            
            <Text color="gray.500" fontSize="xs" textAlign="center">
              Supported Formats: JPEG, JPG, PNG, PDF
              <br />
              Max file size: 5MB
            </Text>
            
            <input
              type="file"
              ref={(el) => (fileInputRefs.current[type] = el)}
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFileChange(e, type)}
            />
          </VStack>
        )}
      </Box>
    );
  }

  if (userData?.kyc_status === 2 || showTimer) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="70vh"
        paddingX={pagePaddingX}
        paddingY={pagePaddingY}
        textAlign="center"
        width="100%"
        overflowX="hidden"
      >
        <Box 
          backgroundColor="white" 
          padding={{ base: "1.5rem", md: "2.5rem" }}
          borderRadius="16px"
          boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          maxWidth="500px"
          width="100%"
          textAlign="center"
        >
          <VStack spacing={6}>
            <Box 
              width="80px" 
              height="80px" 
              borderRadius="50%" 
              backgroundColor="blue.50" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              margin="0 auto"
            >
              <Icon 
                as={SlCloudUpload} 
                boxSize={10} 
                color="#09579a"
              />
            </Box>
            
            <VStack spacing={4}>
              <Heading size="lg" color="#2d3748" fontWeight="700">
                Verification in Progress
              </Heading>
              
              <VStack spacing={3}>
                <Text fontSize="md" color="gray.600" lineHeight="1.6">
                  Your documents are being carefully reviewed by our compliance team.
                </Text>
                
                {/* Always show the estimated completion text */}
                <Box 
                  backgroundColor="blue.50" 
                  padding="1rem" 
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <Text fontSize="sm" color="gray.700">
                    <strong>Estimated Completion:</strong> 1-3 business days
                  </Text>
                </Box>
                
                {/* Show timer when active */}
                {showTimer && (
                  <Box 
                    backgroundColor="blue.50" 
                    padding="1rem" 
                    borderRadius="12px"
                    border="1px solid"
                    borderColor="blue.100"
                  >
                    <Text fontSize="lg" color="gray.700" fontWeight="bold">
                      Redirecting to home: {formatTime(timer)}
                    </Text>
                  </Box>
                )}
                
                <Text fontSize="sm" color="gray.500" mt={2}>
                  You'll receive an email notification once verification is complete.
                  Upon approval, you'll be automatically redirected to your dashboard.
                </Text>
              </VStack>
              
              {/* Back button using CommonButton component */}
              <CommonButton
                text="Back to Login"
                onClick={clearLocalStorageAndNavigate}
                variant="outline"
                mt={4}
                size="lg"
              />
            </VStack>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      paddingX={pagePaddingX}
      paddingY={pagePaddingY}
      overflowX="hidden"
    >
      <Heading 
        size="2xl" 
        marginBottom="1.5rem" 
        color="#2d3748"
        textAlign="center"
        fontWeight="700"
      >
        Complete Your KYC
      </Heading>
      
      <Box width="100%" maxWidth="800px" marginBottom="2rem">
        <Stepper currentStep={2} />
      </Box>

      <Box
        gap="6"
        align="center"
        width="100%"
        margin="auto"
        borderRadius="16px"
        padding={formPadding}
        backgroundColor="white"
        boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      >
        <VStack
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Stack
            direction={{ base: "column", lg: "row" }}
            width="100%"
            justify="space-between"
            alignItems="center"
            gap={6}
            align="center"
            flexWrap="wrap"
          >
            {renderUploadFields}
          </Stack>

          <Box marginTop="1.5rem" width="100%">
            <CommonButton
              text="Submit Documents"
              onClick={handleSubmit}
              disabled={isDisabled}
              variant={isDisabled ? "default" : "gradient"}
              size="lg"
              width={buttonWidth}
            />
          </Box>
        </VStack>
      </Box>
      
      <Box 
        marginTop="2rem" 
        textAlign="center" 
        maxWidth="600px"
        padding="1rem"
        backgroundColor="blue.50"
        borderRadius="12px"
        border="1px solid"
        borderColor="blue.100"
        width="100%"
      >
        <Text fontSize="sm" color="gray.700">
          <strong>Note:</strong> Please ensure all documents are clear, readable, and not expired. 
          Blurry or incomplete documents may delay the verification process.
        </Text>
      </Box>
    </Box>
  );
};